import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiClient } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { logger } from '../services/logger';

const feedbackTypes = ['Bug', 'Feature', 'General'] as const;

type FeedbackType = (typeof feedbackTypes)[number];

export function FeedbackButton() {
  const { addToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('General');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      overlayRef.current?.focus();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFeedbackType('General');
    setMessage('');
    setRating(undefined);
  };

  const closeDialog = () => {
    setIsOpen(false);
    resetForm();
  };

  const handleSubmit= async (event: React.FormEvent) => {
    event.preventDefault();

    if (!message.trim()) {
      addToast('Please enter a feedback message.', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.submitFeedback({
        pageUrl: window.location.pathname + window.location.search,
        feedbackType,
        message: message.trim(),
        rating,
      });

      addToast('Thank you for your feedback!', 'success');
      closeDialog();
    } catch (error) {
      logger.error('Failed to submit feedback', error);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        addToast("You don't have permission to submit feedback.", 'error');
      } else {
        addToast('Failed to submit feedback. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 rounded-full bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
      >
        Feedback
      </button>

      {isOpen && (
        <div
          ref={overlayRef}
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 dark:bg-opacity-80"
          tabIndex={-1}
          onClick={closeDialog}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              closeDialog();
            }
          }}
        >
          <div
            className="mx-4 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Submit Feedback</h2>

            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Feedback type</p>
                <div className="flex gap-2">
                  {feedbackTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFeedbackType(type)}
                      className={`rounded-md px-3 py-2 text-sm font-medium ${
                        feedbackType === type
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="feedback-message">
                  Message
                </label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  rows={5}
                  required
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Rating (optional)</p>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className={`text-2xl ${star <= (rating ?? 0) ? 'text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
                      aria-label={`Set rating ${star}`}
                    >
                      ★
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setRating(undefined)}
                    className="ml-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeDialog}
                  disabled={isSubmitting}
                  className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
